export const REPORT_PAGE_CODES = [
  'executive_overview',
  'ai_portfolio_governance',
  'strategy_roadmap',
  'risk_position',
  'governance_actions_change',
] as const

export type ReportPageCode = typeof REPORT_PAGE_CODES[number]

export const REPORT_PAGE_CATALOGUE: ReadonlyArray<{ code: ReportPageCode; number: number; title: string; question: string }> = [
  { code: 'executive_overview', number: 1, title: 'Executive Overview', question: 'What is happening overall?' },
  { code: 'ai_portfolio_governance', number: 2, title: 'AI Portfolio & Governance', question: 'What AI do we have and what is its governance position?' },
  { code: 'strategy_roadmap', number: 3, title: 'Strategy & Roadmap', question: 'How is AI aligned to strategy and what is planned over time?' },
  { code: 'risk_position', number: 4, title: 'Risk Position', question: 'What risk are we currently carrying?' },
  { code: 'governance_actions_change', number: 5, title: 'Governance Actions & Change', question: 'What requires action and what has changed?' },
]

export type ManagementStatement = {
  text: string
  authorUserId: string
  authorName: string
  recordedAt: string
}

export type ReportingAsset = {
  id: string
  pageCode: ReportPageCode
  storageObjectName: string
  fileName: string
  mimeType: 'image/png'
  width: 1920
  height: 1080
  byteSize: number
  sha256: string
}

export type ReportingPayload = Record<string, unknown> & {
  schemaVersion: string
  asOf: string
  organisation: Record<string, unknown>
  portfolio: Record<string, unknown>
  governance: Record<string, unknown>
  strategy: Record<string, unknown>
  risk: Record<string, unknown>
  comparison: Record<string, unknown> | null
  dataQuality: unknown[]
}

export type ReportPreview = {
  preparedAt: string
  pages: ReportPageCode[]
  payload: ReportingPayload
  fingerprint: string
}

export type ReportingSnapshot = {
  id: string
  organisationId: string
  snapshotNo: number
  snapshotAt: string
  reportingDate: string
  pages: ReportPageCode[]
  templateVersion: string
  metricsSchemaVersion: string
  strategyVersionId: string | null
  previousSnapshotId: string | null
  payload: ReportingPayload
  assetStatus: 'pending' | 'ready'
  managementStatement: ManagementStatement | null
  generatedBy: string
  createdAt: string
  assets: ReportingAsset[]
}

export type SnapshotListItem = Pick<ReportingSnapshot, 'id' | 'snapshotNo' | 'snapshotAt' | 'reportingDate' | 'pages' | 'assetStatus'> & {
  managementStatementIncluded: boolean
  generatedByName: string
}

export function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

export function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : []
}

export function textValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length ? value : fallback
}

export function numberValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : Number(value) || 0
}

export function nullableText(value: unknown): string | null {
  return typeof value === 'string' && value.length ? value : null
}

export function orderedReportPages(value: unknown): ReportPageCode[] {
  const supplied = new Set(Array.isArray(value) ? value : [])
  return REPORT_PAGE_CODES.filter((code) => supplied.has(code))
}

export function pageDefinition(code: ReportPageCode) {
  return REPORT_PAGE_CATALOGUE.find((page) => page.code === code) ?? REPORT_PAGE_CATALOGUE[0]!
}

export function formatBoardDate(value: unknown): string {
  const date = new Date(textValue(value))
  return Number.isNaN(date.getTime()) ? 'Date not recorded' : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatBoardDateTime(value: unknown): string {
  const date = new Date(textValue(value))
  return Number.isNaN(date.getTime()) ? 'Time not recorded' : date.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function metricLabel(value: unknown, state?: unknown): string {
  if (state === 'unknown') return 'Unknown'
  if (state === 'not_recorded') return 'Not recorded'
  if (state === 'not_measured') return 'Not measured'
  if (state === 'not_assessed') return 'Not assessed'
  if (value === null || value === undefined || value === '') return 'Not recorded'
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : 'Not recorded'
}

export function changeLabel(value: unknown): string {
  const amount = numberValue(value)
  if (amount === 0) return 'No change'
  return `${amount > 0 ? '+' : '−'}${Math.abs(amount)}`
}

export function reportFileName(snapshotNo: number, code: ReportPageCode): string {
  const page = pageDefinition(code)
  const slug = page.title.toLowerCase().replaceAll('&', 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  return `${String(page.number).padStart(2, '0')}-${slug}-snapshot-${String(snapshotNo).padStart(4, '0')}.png`
}

export function snapshotZipName(snapshotNo: number): string {
  return `swell-ai-board-report-snapshot-${String(snapshotNo).padStart(4, '0')}.zip`
}
